// Following latest examples from open telemetry and signoz
// https://signoz.io/docs/frontend-monitoring/opentelemetry-web-vitals/

import type { CLSMetric, FCPMetric, INPMetric, LCPMetric, ReportOpts, TTFBMetric } from "@honeycomb/telemetry";
import {
  IResource,
  MeterProvider,
  metrics,
  ObservableGauge,
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
  OTLPMetricExporter,
  PeriodicExportingMetricReader,
} from "@honeycomb/telemetry";

export type WebVitalMetric = CLSMetric | FCPMetric | INPMetric | LCPMetric | TTFBMetric;
export type ReportFunc = (metric: WebVitalMetric) => void;

const addMetricObservable = (meter: ObservableGauge, metric: WebVitalMetric) => {
  meter.addCallback((result) => {
    result.observe(metric.value);
  });
};

const reportWebVitals = (reporter: ReportFunc, opts?: ReportOpts) => {
  const reportOpts: ReportOpts = opts || {
    reportAllChanges: true,
  };

  onCLS(reporter, reportOpts);
  onFCP(reporter, reportOpts);
  onINP(reporter, reportOpts);
  onLCP(reporter, reportOpts);
  onTTFB(reporter, reportOpts);
};

export const createWebVitalMeters = (resource: IResource, apiKey: string, exporterUrl: string) => {
  const metricReader = new PeriodicExportingMetricReader({
    exporter: new OTLPMetricExporter({
      url: `${exporterUrl}/v1/metrics`,
      headers: {
        "signoz-ingestion-key": apiKey,
      },
    }),
    exportIntervalMillis: 10000,
  });

  const meterProvider = new MeterProvider({
    resource,
    readers: [metricReader],
  });

  metrics.setGlobalMeterProvider(meterProvider);

  const meterWebVital = metrics.getMeter("web-vitals");
  const meterCLS = meterWebVital.createObservableGauge("CLS");
  const meterFCP = meterWebVital.createObservableGauge("FCP");
  const meterINP = meterWebVital.createObservableGauge("INP");
  const meterLCP = meterWebVital.createObservableGauge("LCP");
  const meterTTFB = meterWebVital.createObservableGauge("TTFB");

  const sendAnalytics = (metric: WebVitalMetric) => {
    switch (metric.name) {
      case "CLS": {
        addMetricObservable(meterCLS, metric);
        break;
      }
      case "FCP": {
        addMetricObservable(meterFCP, metric);
        break;
      }
      case "INP": {
        addMetricObservable(meterINP, metric);
        break;
      }
      case "LCP": {
        addMetricObservable(meterLCP, metric);
        break;
      }
      case "TTFB": {
        addMetricObservable(meterTTFB, metric);
        break;
      }
    }
  };

  reportWebVitals(sendAnalytics);
};
